import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Edit, Eye, EyeOff, LogOut, Save, X } from "lucide-react";
import EditFloatingActions from "../components/EditFloatingActions";
import { encryptAesGcm } from "../services/crypto_aes";
import { fetchAdmins, updateAdmin } from "../services/admins";
import { getMe, logout } from "../services/session";
import { SECRET_KEY, isCapacitorRuntime } from "../config/vars";
import {
    getAppLockEnabled,
    getBiometricLockStatus,
    formatBiometricUnavailableMessage,
    setAppLockEnabled,
    verifyBiometricLock,
} from "../utils/appLock";
import ThemeSelector from "../components/ThemeSelector";

const phoneRegex = /^(\+?38)?0\d{9}$/;

function formatPhone(phone) {
    let digits = String(phone || "").replace(/\D/g, "");
    if (!digits.startsWith("38")) {
        digits = `38${digits}`;
    }
    return `+${digits}`;
}

export default function AdminProfile() {
    const navigate = useNavigate();
    const [adminData, setAdminData] = useState(null);
    const [editedData, setEditedData] = useState({
        name: "",
        email: "",
        phone: "",
        descriptions: "",
        password_hash: "",
    });
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [message, setMessage] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [appLockReady, setAppLockReady] = useState(false);
    const [appLockEnabled, setAppLockEnabledState] = useState(false);
    const [appLockMessage, setAppLockMessage] = useState("");
    const [loggingOut, setLoggingOut] = useState(false);

    const isMobile = useMemo(() => window.innerWidth < 768, []);

    useEffect(() => {
        let cancelled = false;

        if (!isCapacitorRuntime()) {
            setAppLockReady(true);
            return;
        }

        (async () => {
            try {
                const enabled = await getAppLockEnabled();
                if (!cancelled) {
                    setAppLockEnabledState(enabled);
                }
            } catch (_) {
                // ignore
            } finally {
                if (!cancelled) setAppLockReady(true);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    const loadAdmin = useCallback(async () => {
        try {
            setLoading(true);
            setMessage("");
            const [me, data] = await Promise.all([getMe(), fetchAdmins()]);
            const myId = Number(me?.sub);
            const currentAdmin = Array.isArray(data?.admins)
                ? data.admins.find((admin) => Number(admin.id) === myId) || data.admins[0]
                : null;

            if (!currentAdmin) {
                throw new Error("Дані адміністратора не знайдено");
            }

            setAdminData(currentAdmin);
            setEditedData({
                name: currentAdmin.name || "",
                email: currentAdmin.email || "",
                phone: currentAdmin.phone || "",
                descriptions: currentAdmin.descriptions || "",
                password_hash: "",
            });
        } catch (error) {
            setMessage(`❌ ${error.message || "Не вдалося завантажити профіль"}`);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAdmin();
    }, [loadAdmin]);

    const handleToggleAppLock = async (e) => {
        const next = !!e.target.checked;
        setAppLockMessage("");

        if (!isCapacitorRuntime()) return;

        if (next) {
            const status = await getBiometricLockStatus();
            if (!status.isAvailable) {
                setAppLockMessage(formatBiometricUnavailableMessage(status.code));
                return;
            }

            try {
                await verifyBiometricLock({
                    title: "Увімкнути блокування",
                    subtitle: "Підтвердьте особу",
                });
                await setAppLockEnabled(true);
                setAppLockEnabledState(true);
                setAppLockMessage("✅ Блокування увімкнено");
            } catch (error) {
                setAppLockMessage(`❌ ${error?.message || "Не вдалося увімкнути"}`);
            }
            return;
        }

        await setAppLockEnabled(false);
        setAppLockEnabledState(false);
        setAppLockMessage("✅ Блокування вимкнено");
    };

    const handleEdit = () => {
        setEditing(true);
        setMessage("");
    };

    const handleCancel = () => {
        if (adminData) {
            setEditedData({
                name: adminData.name || "",
                email: adminData.email || "",
                phone: adminData.phone || "",
                descriptions: adminData.descriptions || "",
                password_hash: "",
            });
        }
        setEditing(false);
        setShowPassword(false);
        setMessage("");
    };

    const handleSave = async () => {
        try {
            if (!adminData?.id) {
                throw new Error("Не вдалося визначити ID адміністратора");
            }

            if (!phoneRegex.test(editedData.phone || "")) {
                throw new Error("Невірний формат телефону");
            }

            const payload = {
                name: editedData.name,
                email: editedData.email,
                phone: formatPhone(editedData.phone),
                descriptions: editedData.descriptions,
                status: adminData.status,
                mfa_enabled: adminData.mfa_enabled,
                is_superadmin: adminData.is_superadmin,
            };

            if (editedData.password_hash.trim()) {
                if (!SECRET_KEY) {
                    throw new Error("Не налаштований ключ шифрування (REACT_APP_SECRET_KEY)");
                }
                payload.password_hash = await encryptAesGcm(SECRET_KEY, editedData.password_hash);
            }

            await updateAdmin(adminData.id, payload);
            setMessage("✅ Профіль оновлено успішно");
            setEditing(false);
            setShowPassword(false);
            await loadAdmin();
        } catch (error) {
            setMessage(`❌ ${error.message || "Не вдалося оновити профіль"}`);
        }
    };

    const cardStyle = {
        padding: isMobile ? 18 : 24,
        borderRadius: 24,
        background: "var(--card-bg)",
        border: "1px solid var(--surface-border)",
        boxShadow: "var(--shadow-md)",
        display: "grid",
        gap: 16,
    };

    const fieldStyle = {
        display: "grid",
        gap: 6,
    };

    const inputStyle = {
        width: "100%",
        padding: "10px 12px",
        borderRadius: 14,
        border: "1px solid var(--surface-border)",
        background: "var(--input-bg)",
        color: "var(--text)",
    };

    const handleLogout = useCallback(async () => {
        if (loggingOut) return;
        const confirmed = window.confirm("Вийти з акаунта адміністратора?");
        if (!confirmed) return;
        setLoggingOut(true);
        try {
            await logout();
        } catch (_) {
            // still clear local session even if API fails
        }
        localStorage.clear();
        navigate("/");
    }, [loggingOut, navigate]);

    if (loading) {
        return <p>Завантаження…</p>;
    }

    if (!adminData) {
        return <p style={{ color: "red" }}>❌ Не вдалося завантажити профіль адміністратора.</p>;
    }

    return (
        <div style={{ maxWidth: 880, margin: "0 auto", padding: isMobile ? 12 : 20 }}>
            <div className="admin-profile-card" style={cardStyle}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                    <div>
                        <h2 style={{ margin: 0 }}>Мій профіль</h2>
                        <p style={{ margin: "6px 0 0", color: "var(--text-muted)" }}>Налаштування акаунта адміністратора, теми та локального блокування додатка.</p>
                    </div>
                    <div className={`profile-header-actions ${isMobile && editing ? "profile-mobile-edit-actions" : ""}`} style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {editing ? (
                            <>
                                <button type="button" onClick={handleSave}><Save size={16} /> Зберегти</button>
                                <button type="button" onClick={handleCancel}><X size={16} /> Скасувати</button>
                            </>
                        ) : (
                            <button type="button" onClick={handleEdit}><Edit size={16} /> Редагувати</button>
                        )}
                    </div>
                </div>
                {isMobile && editing ? (
                    <EditFloatingActions onSave={handleSave} onCancel={handleCancel} />
                ) : null}

                <ThemeSelector />

                {isCapacitorRuntime() && appLockReady ? (
                    <div className="profile-settings-block">
                        <strong>Блокування додатка</strong>
                        <label style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                            <input type="checkbox" checked={appLockEnabled} onChange={handleToggleAppLock} />
                            <span>Увімкнути розблокування через біометрію / ПІН</span>
                        </label>
                        {appLockMessage ? <p style={{ margin: "8px 0 0" }}>{appLockMessage}</p> : null}
                    </div>
                ) : null}

                {message ? <div className="message-banner">{message}</div> : null}

                <div style={{ display: "grid", gap: 14 }}>
                    <div style={fieldStyle}>
                        <strong>Ім'я</strong>
                        {editing ? (
                            <input
                                style={inputStyle}
                                type="text"
                                value={editedData.name}
                                onChange={(e) => setEditedData((prev) => ({ ...prev, name: e.target.value }))}
                            />
                        ) : (
                            <span>{adminData.name}</span>
                        )}
                    </div>

                    <div style={fieldStyle}>
                        <strong>Email</strong>
                        {editing ? (
                            <input
                                style={inputStyle}
                                type="email"
                                value={editedData.email}
                                onChange={(e) => setEditedData((prev) => ({ ...prev, email: e.target.value }))}
                            />
                        ) : (
                            <span>{adminData.email}</span>
                        )}
                    </div>

                    <div style={fieldStyle}>
                        <strong>Телефон</strong>
                        {editing ? (
                            <input
                                style={inputStyle}
                                type="text"
                                value={editedData.phone}
                                onChange={(e) => setEditedData((prev) => ({ ...prev, phone: e.target.value }))}
                            />
                        ) : (
                            <span>{adminData.phone}</span>
                        )}
                    </div>

                    <div style={fieldStyle}>
                        <strong>Опис</strong>
                        {editing ? (
                            <textarea
                                style={{ ...inputStyle, minHeight: 100, resize: "vertical" }}
                                value={editedData.descriptions}
                                onChange={(e) => setEditedData((prev) => ({ ...prev, descriptions: e.target.value }))}
                            />
                        ) : (
                            <span>{adminData.descriptions || "-"}</span>
                        )}
                    </div>

                    {editing ? (
                        <div style={fieldStyle}>
                            <strong>Новий пароль</strong>
                            <div style={{ position: "relative" }}>
                                <input
                                    style={{ ...inputStyle, paddingRight: 44 }}
                                    type={showPassword ? "text" : "password"}
                                    value={editedData.password_hash}
                                    onChange={(e) => setEditedData((prev) => ({ ...prev, password_hash: e.target.value }))}
                                    placeholder="Залиште порожнім, якщо не змінюєте"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    style={{
                                        position: "absolute",
                                        right: 10,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        background: "transparent",
                                        border: "none",
                                        padding: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        cursor: "pointer",
                                    }}
                                    aria-label={showPassword ? "Приховати пароль" : "Показати пароль"}
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>

            <div className="config-card profile-logout-card" style={{ marginTop: 16 }}>
                <strong>Сесія</strong>
                <p>Вихід із акаунта. Після виходу потрібно знову увійти.</p>
                <button
                    type="button"
                    className="profile-logout-btn"
                    onClick={handleLogout}
                    disabled={loggingOut}
                >
                    <LogOut size={16} aria-hidden="true" />
                    {loggingOut ? "Вихід..." : "Вийти з акаунта"}
                </button>
            </div>
        </div>
    );
}